import {useReducer, useCallback} from 'react';
import type {ChangeEvent} from 'react';


export type TableSort = {
  sortBy: string;
  sortOrder: 'asc' | 'desc' | 'original';
};

export type TableState = {
  query: string;
  sort: TableSort;
  pageNumber: number;
};

// Initial state
export const initialTableState: TableState = {
  query: '',
  sort: {sortBy: '', sortOrder: 'original'},
  pageNumber: 1,
};

// Table actions
export type TableActions =
  | {
      type: 'SET_QUERY';
      payload: string;
    }
  | {
      type: 'PAGE_CHANGE';
      payload: number;
    }
  | {
      type: 'SORT_CHANGE';
      payload: TableSort;
    }
  | {
      type: 'RESET';
    };

// Reducer function
const tableReducer = (state: TableState, action: TableActions): TableState => {
  switch (action.type) {
    case 'SET_QUERY':
      return {...state, query: action.payload, pageNumber: 1};
    case 'PAGE_CHANGE':
      return {...state, pageNumber: action.payload};
    case 'SORT_CHANGE': {
      const {sortBy, sortOrder} = action.payload;
      return {...state, sort: {sortBy, sortOrder}, pageNumber: 1};
    }
    case 'RESET':
      return initialTableState;
    default:
      return state;
  }
};

export type UseTableManagerType = {
  tableState: TableState;
  handleSetQuery: (query: string | ChangeEvent<HTMLInputElement>) => void;
  handlePageChange: (pageNumber?: number | null | undefined) => void;
  handleSortChange: (
    sortBy: string,
    sortOrder: 'asc' | 'desc' | 'original',
  ) => void;
  handleReset: () => void;
};

export const useTableManager = (
  defaultState?: Partial<TableState>,
): UseTableManagerType => {
  const initialState: TableState = {...initialTableState, ...defaultState};
  const [tableState, dispatch] = useReducer(tableReducer, initialState);

  const handleSetQuery = useCallback(
    (query: string | ChangeEvent<HTMLInputElement>): void => {
      const queryValue = typeof query === 'string' ? query : query.target.value;
      dispatch({type: 'SET_QUERY', payload: queryValue});
    },
    [],
  );

  const handlePageChange = useCallback((pageNumber?: number | null): void => {
    if (!pageNumber) {
      return;
    } // Prevent invalid page numbers
    dispatch({type: 'PAGE_CHANGE', payload: pageNumber});
  }, []);

  const handleSortChange = useCallback(
    (sortBy: string, sortOrder: 'asc' | 'desc' | 'original'): void => {
      dispatch({type: 'SORT_CHANGE', payload: {sortBy, sortOrder}});
    },
    [],
  );

  const handleReset = useCallback((): void => {
    dispatch({type: 'RESET'});
  }, []);

  return {
    tableState,
    handleSetQuery,
    handlePageChange,
    handleSortChange,
    handleReset,
  };
};
